import React from 'react';
import {Formik, Form, Field} from 'formik';
import * as Yup from 'yup';
import {FormControl} from '../form-control';
import {Button, ButtonVariant, ButtonSize, ButtonType} from '../button';
import {Container, ContainerVariant, ContainerSize, ContainerPadding} from '../layout';
import {Heading, Text, HeadingVariant, TextVariant, TextColor, MarginBottom} from '../typography';
import {Icons} from '../../assets/icons';
import {Input} from '../input';

export interface LoginFormData {
  email: string;
}

interface LoginFormProps {
  onSubmit: (values: LoginFormData, actions: any) => Promise<void>;
  className?: string;
}

const initialValues: LoginFormData = {
  email: '',
};

const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Please enter a valid email address with a proper domain')
    .required('Email is required'),
});

export const LoginForm: React.FC<LoginFormProps> = ({onSubmit, className = ''}) => {
  return (
    <Container variant={ContainerVariant.Form} padding={ContainerPadding.MD} className={className}>
      <Container variant={ContainerVariant.Centered} size={ContainerSize.MD}>
        <div className="mb-8">
          <Heading level={2} variant={HeadingVariant.PAGE_TITLE} marginBottom={MarginBottom.SM}>
            Welcome Back!
          </Heading>
          <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
            Enter your email address and we&lsquo;ll send you a 6-digit verification code.
          </Text>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={loginValidationSchema}
          onSubmit={(values, actions) => onSubmit(values, actions)}>
          {({values, errors, touched, isSubmitting, isValid, status}) => {
            const isFormValid = isValid && values.email && Object.keys(errors).length === 0;

            return (
              <Form className="space-y-5">
                <FormControl label="Email" error={touched.email ? errors.email : undefined} required>
                  <Field name="email">
                    {({field}: any) => (
                      <Input
                        {...field}
                        type="email"
                        placeholder="@sample.com"
                        error={!!(touched.email && errors.email)}
                        disabled={isSubmitting}
                        endEnhancer={<img src={Icons.MAIL_ICON} alt="Mail" className="w-5 h-5" />}
                      />
                    )}
                  </Field>
                </FormControl>

                {status && <div className="text-center text-red-600 text-sm bg-red-50 p-3 rounded-lg">{status}</div>}

                <Button
                  type={ButtonType.SUBMIT}
                  variant={ButtonVariant.Primary}
                  size={ButtonSize.MD}
                  fullWidth
                  disabled={!isFormValid}
                  loading={isSubmitting}
                  loadingText="Sending Code...">
                  Login
                </Button>

                <div className="text-center">
                  <Text variant={TextVariant.BODY_SMALL} color={TextColor.MUTED}>
                    Don&lsquo;t have an account?{' '}
                    <a href="/signup" className="text-blue-600 hover:text-blue-800 underline">
                      Sign up
                    </a>
                  </Text>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Container>
    </Container>
  );
};
